import React, { createContext, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash-es';

import { KeyPressEventService } from '../../services/key-press-event-service';
import { KeyHandlerService } from '../../services/key-handler-service';
import { isKeyEventOnInput } from '../../services/hotkey-service/should-add-event-key';
import { DisplayGridRecordPropType } from '../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../prop-types/display-grid-group-record';
import { GridHelperService } from '../../services/grid-helper-service';
import { getAdjustedDisplayInformationFromKeypress } from './services/key-pressed-handler-service';
import { OnKeyPressSubject } from './observables/on-key-press-subject';
import { OnRowClickSubject } from './observables/on-row-click-subject';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

export const RowContext = createContext();
const { Provider } = RowContext;

export const RowContextProvider = ({
  children,
  expandedRows,
  onExpandedRowsChanged,
  selected: selectedProp = emptyArray,
  onSelectedChanged,
  data,
  disableMultiSelect = false,
  disabledIds = emptyObject,
}) => {
  const lastSelectedDatum = useRef(null);
  const [expanded, setExpanded] = useState(new Set(expandedRows));
  const [expandedGroupRows, setExpandedGroupRows] = useState(new Set());
  const [selectedGroupRows, setSelectedGroupRows] = useState(new Set());
  const [totalSelectedGroups, setTotalSelectedGroups] = useState(new Set());
  const [selected, setSelected] = useState(new Set(selectedProp));

  useEffect(() => {
    setSelected((prevSelected) => {
      if (prevSelected.size === selectedProp.length) {
        const newSelectedSet = new Set([...prevSelected, ...selectedProp]);
        if (newSelectedSet.size === prevSelected.size) {
          return prevSelected;
        }
      }
      return new Set(selectedProp);
    });
  }, [selectedProp]);

  useEffect(() => {
    const firstDataItem = data[0] ?? [];
    const hasGroupByData = firstDataItem[0]?.groupById;
    if (hasGroupByData) {
      const groupByIdMetaData = data.map(([groupMetadata]) => groupMetadata.groupById);
      setExpandedGroupRows(new Set(groupByIdMetaData));
    }
  }, [setExpandedGroupRows, data]);

  // When a Group By is active we must use the data in its non grouped form
  const coercedData = useMemo(() => {
    return data.filter((d) => !d.groupById);
  }, [data]);

  const debouncedKeyboard = useRef();
  const updateSelected = useCallback(
    (newSelected, isFromKeyboard) => {
      const triggerChange = () => {
        if (onSelectedChanged) {
          onSelectedChanged(newSelected);
        }
      };

      if (isFromKeyboard) {
        if (debouncedKeyboard.current) {
          debouncedKeyboard.current.cancel();
        }
        debouncedKeyboard.current = debounce(triggerChange, 100);
        debouncedKeyboard.current();
      } else {
        triggerChange();
      }

      setSelected(new Set(newSelected));
    },
    [onSelectedChanged],
  );

  const keyboardWasPressed = useCallback(
    (event) => {
      const keypress = KeyPressEventService.transformEventToArrowPress(event);

      if (isKeyEventOnInput()) {
        return;
      }
      if (keypress.isArrowKey) {
        event.preventDefault();
        const { updatedSelected, updatedCurrentlySelectedId } = getAdjustedDisplayInformationFromKeypress(
          keypress,
          selected,
          coercedData,
          lastSelectedDatum?.current,
          disableMultiSelect,
        );
        updateSelected(updatedSelected, true);
        lastSelectedDatum.current = updatedCurrentlySelectedId;
      }
    },
    [coercedData, disableMultiSelect, selected, updateSelected],
  );

  useEffect(() => {
    const subscription = OnKeyPressSubject.subscribe(keyboardWasPressed);

    return () => subscription.unsubscribe();
  }, [keyboardWasPressed]);

  const onRowClick = useCallback(
    ({ event, rowData }) => {
      if (rowData.groupById) {
        const newGroupRows = new Set(selectedGroupRows);
        if (newGroupRows.has(rowData.groupById)) {
          newGroupRows.delete(rowData.groupById);
          setTotalSelectedGroups((groups) => {
            const newTotalSelectedGroups = new Set(groups);
            newTotalSelectedGroups.delete(rowData.groupById);
            return newTotalSelectedGroups;
          });
        } else {
          newGroupRows.add(rowData.groupById);
        }
        setSelectedGroupRows(newGroupRows);
      }

      const isCurrentlySelected = selected.has(rowData.id) || selectedGroupRows.has(rowData.groupById);

      if (event.shiftKey && !disableMultiSelect && !rowData.groupById) {
        let dataIds = coercedData.map((datum) => GridHelperService.getPrimaryRow(datum).id);
        const [firstItem] = coercedData;
        const isGroupedData = Array.isArray(firstItem) && firstItem[0]?.groupById;
        if (isGroupedData) {
          // flatten out grouped data
          dataIds = coercedData.reduce((acc, grouped) => {
            grouped
              .filter((item) => !item.groupById)
              .forEach((groupedItem) => {
                acc.push(GridHelperService.getPrimaryRow(groupedItem).id);
              });
            return acc;
          }, []);
        }

        const currentRowIndex = dataIds.indexOf(rowData.id);
        const lastSelectedDatumIndex = lastSelectedDatum.current === null ? null : dataIds.indexOf(lastSelectedDatum.current);
        const lastSelectedIndex = lastSelectedDatumIndex === null ? dataIds.indexOf(rowData.id) : lastSelectedDatumIndex;
        const startIndex = Math.min(currentRowIndex, lastSelectedIndex);
        const endIndex = Math.max(currentRowIndex, lastSelectedIndex);

        if (startIndex === -1 || endIndex === -1 || endIndex >= dataIds.length) {
          return;
        }

        const idsToSelect = dataIds.slice(startIndex, endIndex + 1);

        if (isCurrentlySelected) {
          // Deselecting, subtract idsToSelect from the currentlySelectedDataIds list
          const idsToDeselect = new Set(idsToSelect);
          const updatedSelectedDataIds = [...selected].reduce((acc, id) => {
            if (!idsToDeselect.has(id)) {
              acc.push(id);
            }

            return acc;
          }, []);

          updateSelected(updatedSelectedDataIds);
        } else {
          updateSelected([...new Set([...idsToSelect, ...selected])]);
        }

        lastSelectedDatum.current = rowData.id;
        return;
      }

      lastSelectedDatum.current = rowData.id;

      const isSelected =
        selected.size <= 1 || (KeyHandlerService.eventHasControlPressed(event) && !disableMultiSelect) ? !isCurrentlySelected : true;
      const newSelectedIds = new Set(isSelected && rowData.id ? [rowData.id] : []);

      // shift is dominant
      if (KeyHandlerService.eventHasControlPressed(event) && !disableMultiSelect) {
        const newSelectedSet = new Set(selected);
        newSelectedSet.delete(rowData.id);
        newSelectedSet.forEach((id) => newSelectedIds.add(id));
      }

      if (rowData.groupById) {
        const [, ...itemsToUpdate] = data
          .find((group) => group[0].groupById === rowData.groupById)
          .map((d) => GridHelperService.getPrimaryRow(d).id);
        itemsToUpdate.forEach((id) => (isCurrentlySelected ? newSelectedIds.delete(id) : newSelectedIds.add(id)));
      }

      updateSelected([...newSelectedIds]);
    },
    [selected, selectedGroupRows, disableMultiSelect, updateSelected, coercedData, data],
  );

  useEffect(() => {
    const subscription = OnRowClickSubject.subscribe(onRowClick);

    return () => subscription.unsubscribe();
  }, [onRowClick]);

  const value = useMemo(
    () => ({
      expanded,
      expandedGroupRows,
      setExpandedGroupRows,
      onExpandedRowsChanged: onExpandedRowsChanged ?? setExpanded,
      selected,
      disabledIds,
      updateSelected,
      totalSelectedGroups,
      setTotalSelectedGroups,
      selectedGroupRows,
      setSelectedGroupRows,
    }),
    [
      expanded,
      expandedGroupRows,
      setExpandedGroupRows,
      onExpandedRowsChanged,
      selected,
      disabledIds,
      updateSelected,
      totalSelectedGroups,
      setTotalSelectedGroups,
      selectedGroupRows,
      setSelectedGroupRows,
    ],
  );

  return <Provider value={value}>{children}</Provider>;
};

RowContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  expandedRows: PropTypes.array,
  onExpandedRowsChanged: PropTypes.func,
  selected: PropTypes.array,
  onSelectedChanged: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
  disableMultiSelect: PropTypes.bool,
  disabledIds: PropTypes.object,
};

export const rowContext = {
  RowContext,
  RowContextProvider: React.memo(RowContextProvider),
};
